import React, {FC, useRef} from "react";
import {Card, Colors, Container, Divider, IconChevronsIn, Text, useMenuCollapsed} from "@bounce/shared/ui";
import InputUtils from "@bounce/shared/helpers/InputUtils";
import {
  partnerInfoHeaderContainerStyle,
  partnerInfoLogoContainerStyle,
  partnerInfoNameStyle,
  partnerInfoTypeStyle,
} from "./styles";

interface PartnerInfoHeaderProps {
  partnerName: string;
  partnerSymbol?: string;
  email: string;
  partnerType?: string;
  firstPartyEnabled?: boolean;
  onSwitchClick: (event: React.MouseEvent<HTMLElement>) => void;
  showSwitcher?: boolean;
}

const PartnerInfoHeader: FC<PartnerInfoHeaderProps> = ({
  partnerName,
  partnerSymbol,
  email,
  partnerType,
  firstPartyEnabled,
  onSwitchClick,
  showSwitcher = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const collapsed = useMenuCollapsed();

  const handleClick = () => {
    if (containerRef.current && showSwitcher) {
      onSwitchClick({currentTarget: containerRef.current} as unknown as React.MouseEvent<HTMLElement>);
    }
  };

  if (collapsed) {
    return (
      <div
        ref={containerRef}
        onClick={showSwitcher ? handleClick : undefined}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          cursor: showSwitcher ? "pointer" : "default",
        }}
      >
        <Container sx={{...partnerInfoLogoContainerStyle, width: "36px", height: "36px"}}>
          <img
            src={partnerSymbol}
            alt={partnerName}
            style={{maxWidth: "100%", maxHeight: "100%", objectFit: "contain"}}
          />
        </Container>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{width: "100%"}}>
      <Card onClick={showSwitcher ? handleClick : undefined} sx={partnerInfoHeaderContainerStyle}>
        <Container row sx={{width: "100%"}}>
          <Container sx={{gap: "6px", flex: 1, alignItems: "flex-start"}}>
            <Container sx={partnerInfoLogoContainerStyle}>
              <img
                src={partnerSymbol}
                alt={partnerName}
                style={{maxWidth: "100%", maxHeight: "100%", objectFit: "contain"}}
              />
            </Container>
            <Container sx={{gap: "0px", flexDirection: "column", alignItems: "flex-start"}}>
              <Text size={14} variant="inter600" sx={partnerInfoNameStyle}>
                {partnerName}
              </Text>
              {partnerType && (
                <Text size={14} variant="inter500" sx={partnerInfoTypeStyle}>
                  {firstPartyEnabled ? "First Party" : "Third Party"} {InputUtils.prettierSnakeCase(partnerType)}
                </Text>
              )}
            </Container>
          </Container>
          {showSwitcher && (
            <Container sx={{marginTop: "40px", "& svg path": {stroke: Colors.tertiary._600}}}>
              <IconChevronsIn width={16} height={16} />
            </Container>
          )}
        </Container>
        <Divider sx={{width: "100%", borderColor: Colors.tertiary._300}} />
        <Text size={12} variant="inter400" sx={{color: Colors.tertiary._600, width: "100%"}}>
          {email}
        </Text>
      </Card>
    </div>
  );
};

export default PartnerInfoHeader;
